"use client";

import React from "react";
import { useForm } from "react-hook-form";
import LoadingSpinner from "@/components/shared/LoadingSpinner";
import TurnstileField from "@/components/shared/TurnstileField";
import { useTurnstile } from "@/hooks/useTurnstile";
import { validateWorkEmail } from "@/lib/validators/email";
import type { NewsletterFormState } from "@/types/newsletter";
import { logger } from "@/utils/logger";

type NewsletterFormData = {
  email: string;
};

interface NewsletterSignupProps {
  variant?: "inline" | "stacked";
  source?: string;
  buttonText?: string;
  onSuccess?: (data: { subscriberId: string; downloadToken?: string }) => void;
}

export default function NewsletterSignup({
  variant = "inline",
  source = "website",
  buttonText = "Subscribe",
  onSuccess,
}: NewsletterSignupProps) {
  const [formState, setFormState] = React.useState<NewsletterFormState>({
    status: "idle",
  });
  const [isAwaitingVerification, setIsAwaitingVerification] =
    React.useState(false);
  const {
    isVisible: isTurnstileVisible,
    token: turnstileToken,
    message: turnstileMessage,
    state: turnstileState,
    containerRef: turnstileContainerRef,
    clear: clearTurnstile,
    handleError: handleTurnstileError,
    handleExpire: handleTurnstileExpire,
    handleVerify: handleTurnstileVerify,
    requestVerification: requestTurnstileVerification,
  } = useTurnstile({
    requiredMessage:
      "Running a quick security check. Your signup will continue automatically.",
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<NewsletterFormData>({
    defaultValues: {
      email: "",
    },
  });

  const isSubmitting = formState.status === "loading" || isAwaitingVerification;

  const onSubmit = React.useCallback(
    async (data: NewsletterFormData) => {
      const normalizedEmail = data.email.trim();
      const emailValidation = validateWorkEmail(normalizedEmail);

      if (!emailValidation.valid) {
        setFormState({
          status: "error",
          message:
            emailValidation.error || "Please enter a valid work email address.",
        });
        return;
      }

      if (formState.status === "error") {
        setFormState({ status: "idle" });
      }

      if (!turnstileToken) {
        setIsAwaitingVerification(true);
        requestTurnstileVerification();
        return;
      }

      setIsAwaitingVerification(false);
      setFormState({ status: "loading" });

      try {
        const response = await fetch("/api/beehiiv/subscribe", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            email: normalizedEmail,
            source,
            tags: source === "free-templates" ? ["lead-magnet"] : [],
            turnstileToken: turnstileToken,
          }),
        });

        const result = await response.json();

        if (response.ok && result.success) {
          const successMessage =
            result.message ||
            (source === "free-templates"
              ? "Check your email for the download link!"
              : "Thank you! Check your email for confirmation.");

          setFormState({
            status: "success",
            message: successMessage,
          });
          reset();
          clearTurnstile();
          onSuccess?.({
            subscriberId: result.subscriberId,
            downloadToken: result.downloadToken,
          });

          // Redirect to success page for free-templates
          if (
            source === "free-templates" &&
            typeof window !== "undefined" &&
            !result.alreadySubscribed
          ) {
            setTimeout(() => {
              window.location.href = "/free-templates/success";
            }, 1500);
          }
        } else {
          if (result.code === "TURNSTILE_FAILED") {
            clearTurnstile();
            requestTurnstileVerification(
              "Please retry the quick security check below.",
            );
          }
          setFormState({
            status: "error",
            message: result.error || "Failed to subscribe. Please try again.",
          });
        }
      } catch (error) {
        logger.error("Newsletter subscription error", {
          error: error instanceof Error ? error : new Error(String(error)),
        });
        setFormState({
          status: "error",
          message: "An error occurred. Please try again later.",
        });
      }
    },
    [
      formState.status,
      onSuccess,
      reset,
      source,
      clearTurnstile,
      requestTurnstileVerification,
      turnstileToken,
    ],
  );

  React.useEffect(() => {
    if (!isAwaitingVerification || !turnstileToken) {
      return;
    }

    setIsAwaitingVerification(false);
    void handleSubmit(onSubmit)();
  }, [handleSubmit, isAwaitingVerification, onSubmit, turnstileToken]);

  const isStacked = variant === "stacked";

  return (
    <div className="w-full min-w-0">
      {formState.status === "success" ? (
        <div
          className={`w-full min-w-0 py-4 ${isStacked ? "text-center" : "text-center lg:text-left"}`}
        >
          <p className="break-words whitespace-normal text-green-500 text-sm leading-6">
            {formState.message}
          </p>
        </div>
      ) : (
        <>
          <form
            onSubmit={handleSubmit(onSubmit)}
            className={
              isStacked
                ? "flex w-full flex-col gap-3"
                : "flex w-full min-w-0 flex-col gap-3 sm:flex-row"
            }
          >
            <div className={isStacked ? "w-full min-w-0" : "min-w-0 flex-1"}>
              <input
                {...register("email", {
                  required: "Please enter your email address",
                  pattern: {
                    value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                    message: "Please enter a valid email address",
                  },
                })}
                id={isStacked ? "newsletter-email-stacked" : "newsletter-email-inline"}
                type="email"
                autoComplete="email"
                placeholder="Enter your email"
                disabled={isSubmitting}
                className="w-full h-12 px-4 py-3 bg-slate-900 border border-slate-700 rounded-lg text-white placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
                aria-invalid={errors.email ? "true" : "false"}
                aria-describedby={errors.email ? "email-error" : undefined}
              />
              {errors.email && (
                <p
                  id="email-error"
                  role="alert"
                  className="text-red-500 text-sm mt-1"
                >
                  {errors.email.message}
                </p>
              )}
            </div>
            <button
              type="submit"
              disabled={isSubmitting}
              className={`${isStacked ? "w-full" : ""} h-12 px-6 py-3 bg-[#7f56d9] hover:bg-[#6d47b3] rounded-full text-white font-semibold text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#a78bfa] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 whitespace-nowrap`}
            >
              {isSubmitting ? (
                <>
                  <LoadingSpinner />
                  <span>
                    {formState.status === "loading"
                      ? "Submitting..."
                      : "Securing..."}
                  </span>
                </>
              ) : (
                buttonText
              )}
            </button>
          </form>
          <TurnstileField
            action="newsletter_signup"
            className="mt-3 min-w-0"
            containerRef={turnstileContainerRef}
            id={
              isStacked
                ? "newsletter-turnstile-stacked"
                : "newsletter-turnstile-inline"
            }
            message={turnstileMessage}
            onError={handleTurnstileError}
            onExpire={handleTurnstileExpire}
            onVerify={handleTurnstileVerify}
            state={turnstileState}
            visible={isTurnstileVisible}
          />
          {formState.status === "error" && formState.message && (
            <p
              className="mt-2 break-words whitespace-normal text-red-500 text-sm leading-6"
              role="alert"
            >
              {formState.message}
            </p>
          )}
        </>
      )}
    </div>
  );
}
